export interface CameraSettings {
  readonly fov: number;
  readonly distance: number;
  readonly height: number;
  readonly angleDeg: number;
  readonly stiffness: number;
  readonly swivelSpeed: number;
  readonly transitionSpeed: number;
  readonly invertSwivel: boolean;
}

export type CameraNumericSetting = Exclude<keyof CameraSettings, 'invertSwivel'>;

/** FOV is horizontal degrees; distance and height are Unreal Units; angle is degrees. */
export const DEFAULT_CAMERA_SETTINGS: CameraSettings = Object.freeze({
  fov: 110,
  distance: 270,
  height: 100,
  angleDeg: -3,
  stiffness: 0.35,
  swivelSpeed: 7,
  transitionSpeed: 2,
  invertSwivel: false,
});

export const CAMERA_SETTING_RANGES = Object.freeze({
  fov: Object.freeze({ min: 60, max: 110, step: 1 }),
  distance: Object.freeze({ min: 100, max: 400, step: 10 }),
  height: Object.freeze({ min: 40, max: 200, step: 10 }),
  angleDeg: Object.freeze({ min: -15, max: 0, step: 1 }),
  stiffness: Object.freeze({ min: 0, max: 1, step: 0.05 }),
  swivelSpeed: Object.freeze({ min: 1, max: 10, step: 0.1 }),
  transitionSpeed: Object.freeze({ min: 1, max: 2, step: 0.1 }),
}) satisfies Readonly<Record<CameraNumericSetting, Readonly<{ min: number; max: number; step: number }>>>;

const NUMERIC_KEYS = Object.keys(CAMERA_SETTING_RANGES) as CameraNumericSetting[];

/** Recover valid persisted fields independently, without coercing external values. */
export function parseCameraSettings(value: unknown): CameraSettings {
  if (!isRecord(value)) return DEFAULT_CAMERA_SETTINGS;
  const settings = { ...DEFAULT_CAMERA_SETTINGS };
  for (const key of NUMERIC_KEYS) {
    const candidate = value[key];
    if (validNumericSetting(key, candidate)) settings[key] = candidate;
  }
  if (typeof value.invertSwivel === 'boolean') settings.invertSwivel = value.invertSwivel;
  return Object.freeze(settings);
}

export function validateCameraSettings(value: unknown): asserts value is CameraSettings {
  if (!isRecord(value)) throw new TypeError('Camera settings must be an object.');
  for (const key of NUMERIC_KEYS) {
    if (!validNumericSetting(key, value[key])) {
      const { min, max } = CAMERA_SETTING_RANGES[key];
      throw new RangeError(`Camera setting ${key} must be a finite number between ${min} and ${max}.`);
    }
  }
  if (typeof value.invertSwivel !== 'boolean') throw new TypeError('Camera setting invertSwivel must be a boolean.');
}

function validNumericSetting(key: CameraNumericSetting, value: unknown): value is number {
  const { min, max } = CAMERA_SETTING_RANGES[key];
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
